import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Polygon, useMapEvents } from 'react-leaflet';
import { Plus, Trash2, Check, X, Edit } from 'lucide-react';

export type ServiceArea = {
    id: string;
    name: string;
    /** Closed polygon ring in GeoJSON order: [lng, lat][] */
    coords: [number, number][];
};

const COLORS = ['#0fb37a', '#3b82f6', '#f59e0b', '#ec4899', '#8b5cf6', '#06b6d4', '#ef4444'];

/** Build a closed rectangular polygon ring in GeoJSON [lng,lat] from two [lat,lng] corners */
function makeRect(c1: [number, number], c2: [number, number]): [number, number][] {
    const [lat1, lng1] = c1;
    const [lat2, lng2] = c2;
    const minLat = Math.min(lat1, lat2), maxLat = Math.max(lat1, lat2);
    const minLng = Math.min(lng1, lng2), maxLng = Math.max(lng1, lng2);
    return [
        [minLng, minLat], [maxLng, minLat],
        [maxLng, maxLat], [minLng, maxLat],
        [minLng, minLat],
    ];
}

/** Convert GeoJSON [lng,lat] ring to Leaflet [lat,lng] positions */
function toLeaflet(coords: [number, number][]): [number, number][] {
    return coords.map(([lng, lat]) => [lat, lng]);
}

type Step = 'idle' | 'c1' | 'c2';

function DrawCtrl({ step, onC1, onC2, onMove }: {
    step: Step;
    onC1: (p: [number, number]) => void;
    onC2: (p: [number, number]) => void;
    onMove: (p: [number, number]) => void;
}) {
    const map = useMapEvents({
        click: (e) => {
            if (step === 'c1') onC1([e.latlng.lat, e.latlng.lng]);
            else if (step === 'c2') onC2([e.latlng.lat, e.latlng.lng]);
        },
        mousemove: (e) => {
            if (step === 'c2') onMove([e.latlng.lat, e.latlng.lng]);
        },
    });
    useEffect(() => {
        const el = map.getContainer();
        el.style.cursor = step !== 'idle' ? 'crosshair' : '';
        return () => { el.style.cursor = ''; };
    }, [map, step]);
    return null;
}

interface Props {
    areas: ServiceArea[];
    onChange: (areas: ServiceArea[]) => void;
}

export default function ServiceAreasMap({ areas, onChange }: Props) {
    const [step, setStep] = useState<Step>('idle');
    const [c1, setC1] = useState<[number, number] | null>(null);
    const [cursor, setCursor] = useState<[number, number] | null>(null);
    const [editId, setEditId] = useState<string | null>(null);
    const [editName, setEditName] = useState('');

    const startDraw = () => { setStep('c1'); setC1(null); setCursor(null); };
    const cancel = () => { setStep('idle'); setC1(null); setCursor(null); };

    const handleC1 = (p: [number, number]) => { setC1(p); setStep('c2'); };
    const handleC2 = (p: [number, number]) => {
        if (!c1) return;
        onChange([...areas, {
            id: `area-${Date.now()}`,
            name: `Area ${areas.length + 1}`,
            coords: makeRect(c1, p),
        }]);
        cancel();
    };

    const remove = (id: string) => onChange(areas.filter(a => a.id !== id));
    const beginEdit = (a: ServiceArea) => { setEditId(a.id); setEditName(a.name); };
    const commitEdit = () => {
        onChange(areas.map(a => a.id === editId ? { ...a, name: editName } : a));
        setEditId(null);
    };

    const previewPoly = c1 && cursor ? toLeaflet(makeRect(c1, cursor)) : null;

    return (
        <div className="space-y-3">
            {/* Map */}
            <div className="relative rounded-xl overflow-hidden border border-white/10" style={{ height: 320 }}>
                <MapContainer
                    center={[4.2, 73.52]}
                    zoom={11}
                    zoomControl={false}
                    style={{ height: '100%', width: '100%' }}
                >
                    <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution="© OpenStreetMap"
                    />
                    <DrawCtrl step={step} onC1={handleC1} onC2={handleC2} onMove={setCursor} />

                    {/* Existing areas */}
                    {areas.map((area, i) => (
                        <Polygon
                            key={area.id}
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            positions={toLeaflet(area.coords) as any}
                            pathOptions={{
                                color: COLORS[i % COLORS.length],
                                fillOpacity: 0.18,
                                weight: 2,
                            }}
                        />
                    ))}

                    {/* Preview rectangle */}
                    {previewPoly && (
                        <Polygon
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            positions={previewPoly as any}
                            pathOptions={{ color: '#0fb37a', fillOpacity: 0.08, dashArray: '6,4', weight: 2 }}
                        />
                    )}
                </MapContainer>

                {/* Draw step hint */}
                {step !== 'idle' && (
                    <div
                        className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] flex items-center gap-3 px-4 py-2 rounded-xl shadow-xl"
                        style={{ background: 'rgba(15,26,71,0.92)', border: '1px solid rgba(255,255,255,0.15)' }}
                    >
                        <span className="text-sm text-white select-none">
                            {step === 'c1' ? '📍 Click first corner of the area' : '📍 Click second corner to finish'}
                        </span>
                        <button type="button" onClick={cancel} className="text-white/50 hover:text-white transition-colors">
                            <X size={14} />
                        </button>
                    </div>
                )}

                {/* Zoom hint */}
                <div className="absolute bottom-3 right-3 z-[1000] text-xs text-white/30 bg-black/40 rounded-lg px-2 py-1 pointer-events-none select-none">
                    scroll to zoom
                </div>
            </div>

            {/* Add area button */}
            {step === 'idle' && (
                <button
                    type="button"
                    onClick={startDraw}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary-500/15 border border-primary-500/30 text-primary-300 text-sm font-medium hover:bg-primary-500/25 transition-colors"
                >
                    <Plus size={15} />
                    Draw New Service Area
                </button>
            )}

            {/* Area list */}
            {areas.length > 0 ? (
                <div className="space-y-2 pt-1">
                    <p className="text-[11px] font-semibold text-white/40 uppercase tracking-widest">
                        Configured Areas ({areas.length})
                    </p>
                    {areas.map((area, i) => (
                        <div
                            key={area.id}
                            className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/10"
                        >
                            <span
                                className="w-2.5 h-2.5 rounded-full shrink-0 ring-2 ring-white/10"
                                style={{ background: COLORS[i % COLORS.length] }}
                            />

                            {editId === area.id ? (
                                <>
                                    <input
                                        autoFocus
                                        type="text"
                                        value={editName}
                                        onChange={e => setEditName(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') commitEdit();
                                            if (e.key === 'Escape') setEditId(null);
                                        }}
                                        className="flex-1 h-7 px-2 rounded-lg bg-white/10 border border-white/20 text-white text-sm focus:outline-none focus:border-primary-500/50"
                                    />
                                    <button type="button" onClick={commitEdit} className="text-primary-400 hover:text-primary-300 transition-colors">
                                        <Check size={14} />
                                    </button>
                                    <button type="button" onClick={() => setEditId(null)} className="text-white/40 hover:text-white transition-colors">
                                        <X size={14} />
                                    </button>
                                </>
                            ) : (
                                <>
                                    <span className="flex-1 text-sm text-white">{area.name}</span>
                                    <button
                                        type="button"
                                        onClick={() => beginEdit(area)}
                                        className="text-white/30 hover:text-primary-400 transition-colors"
                                        title="Rename"
                                    >
                                        <Edit size={13} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => remove(area.id)}
                                        className="text-white/30 hover:text-red-400 transition-colors"
                                        title="Remove"
                                    >
                                        <Trash2 size={13} />
                                    </button>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            ) : step === 'idle' ? (
                <p className="text-sm text-white/30 text-center py-3">
                    No service areas defined yet. Click "Draw New Service Area" to start.
                </p>
            ) : null}
        </div>
    );
}
